import { useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { planService } from '../services';
import { toast } from '../store/toastStore';

export function usePlans() {
  return useQuery({
    queryKey: ['plans'],
    queryFn: planService.list,
  });
}

export function usePlan(id) {
  const queryClient = useQueryClient();
  const prevStatusRef = useRef(null);

  const query = useQuery({
    queryKey: ['plan', id],
    queryFn: () => planService.get(id),
    enabled: Boolean(id),
    // Poll every 25 s while the plan is still generating
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'generating' ? 25000 : false;
    },
  });

  // Fire toast exactly once when status transitions out of 'generating'
  useEffect(() => {
    const plan = query.data;
    if (!plan) return;
    const prev = prevStatusRef.current;
    const curr = plan.status;
    if (prev === 'generating' && curr !== 'generating') {
      if (curr === 'active') {
        toast.success(`Plan for "${plan.goal_title}" is ready!`);
      } else if (curr === 'failed') {
        toast.error(plan.error_message || 'Plan generation failed. Please try again.');
      }
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    }
    prevStatusRef.current = curr;
  }, [query.data, queryClient]);

  return query;
}

export function useGeneratePlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: planService.generate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useRegeneratePlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: planService.regenerate,
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      queryClient.invalidateQueries({ queryKey: ['plan', id] });
    },
    onError: (err) => {
      const msg = err.response?.data?.error?.message || 'Failed to regenerate plan.';
      toast.error(msg);
    },
  });
}

export function usePlanProgress(id) {
  return useQuery({
    queryKey: ['plan-progress', id],
    queryFn: () => planService.getProgress(id),
    enabled: Boolean(id),
  });
}

export function useLogProgress(planId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => planService.logProgress(planId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan-progress', planId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useDeleteProgress(planId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entryId) => planService.deleteProgress(planId, entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan-progress', planId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}
